import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Shield, MessageCircle } from 'lucide-react';
import { gemstones, contactInfo } from '../data/mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const GemstoneSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (isAutoPlaying) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % gemstones.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % gemstones.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + gemstones.length) % gemstones.length);
    setIsAutoPlaying(false);
  };

  const getVisibleGemstones = () => {
    const visible = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + gemstones.length) % gemstones.length;
      visible.push({ ...gemstones[index], position: i });
    }
    return visible;
  };

  const handleWhatsAppInquiry = (gemstone) => {
    const message = `Hi, I'm interested in ${gemstone.name} (${gemstone.sanskritName}). Please provide more details.`;
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <section className="gemstone-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Premium Gemstone Collection</h2>
          <p className="section-description">
            Certified, energized gemstones to harmonize your planetary influences
          </p>
          <div className="gemstone-trust-badges">
            <Badge variant="outline" className="trust-badge">
              <Shield size={16} />
              100% Certified & Authentic
            </Badge>
            <Badge variant="outline" className="trust-badge">
              Lab-Tested Quality
            </Badge>
          </div>
        </div>

        <div className="gemstone-slider-container"
          onMouseEnter={() => setIsAutoPlaying(false)}
          onMouseLeave={() => setIsAutoPlaying(true)}
        >
          <button className="slider-nav prev" onClick={prevSlide}>
            <ChevronLeft size={32} />
          </button>

          <div className="gemstone-track">
            {getVisibleGemstones().map((gemstone) => (
              <div
                key={gemstone.id}
                className={`gemstone-card ${gemstone.position === 0 ? 'active' : ''} position-${gemstone.position}`}
              >
                <div className="gemstone-image-wrapper">
                  <img src={gemstone.image} alt={gemstone.name} className="gemstone-image" />
                  <div className="gemstone-glow" />
                </div>
                <div className="gemstone-content">
                  <h3 className="gemstone-name">{gemstone.name}</h3>
                  <p className="gemstone-sanskrit">{gemstone.sanskritName}</p>
                  {gemstone.benefit && (
                    <p className="gemstone-benefit">{gemstone.benefit}</p>
                  )}
                  {(gemstone.weight || gemstone.planet) && (
                    <div className="gemstone-details">
                      {gemstone.weight && (
                        <span className="gemstone-weight">{gemstone.weight}</span>
                      )}
                      {gemstone.planet && (
                        <span className="gemstone-planet">• {gemstone.planet}</span>
                      )}
                    </div>
                  )}
                  {gemstone.priceStart && (
                    <div className="gemstone-price">Starting from ₹{gemstone.priceStart}</div>
                  )}
                  <Button 
                    className="gemstone-cta"
                    onClick={() => handleWhatsAppInquiry(gemstone)}
                  >
                    <MessageCircle size={18} />
                    Inquire Now
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <button className="slider-nav next" onClick={nextSlide}>
            <ChevronRight size={32} />
          </button>
        </div>

        <div className="slider-dots">
          {gemstones.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => {
                setCurrentIndex(index);
                setIsAutoPlaying(false);
              }}
            />
          ))}
        </div>

        <p className="gemstone-footer-text">
          All gemstones come with authenticity certificates • Energized & activated by expert astrologers
        </p>
      </div>
    </section>
  );
};

export default GemstoneSlider;